// 170 §1a — THE TWO DOCUMENTS THE EMAIL ARCHIVE FILES, EXTRACTED.
// PURE: no React/DOM/network, so the fixtures import the same code the browser
// runs (the htmlEscape.mjs rule).
//
// ── WHY IT MOVED, AND WHAT DID NOT CHANGE ───────────────────────────────────
// These builders lived in `emailArchiveClient.ts`, which imports `../api` and
// `../i18n` — so a node fixture could not load them, and the only way to test
// the filed document would have been to REBUILD IT IN THE FIXTURE. A second
// copy of the thing under test is how a fixture comes to prove something the
// application does not do (`probe-server.js`'s "one reader, one gate").
//
// This is a MOVE. The markup is byte-for-byte what `emailArchiveClient.ts`
// emitted; the two dependencies it could not bring — the dictionary and the
// colour tokens — arrive as arguments instead of imports, and the thin
// wrappers in that file supply `tImperative` and `CT` exactly as before.
//
// `escapeText` escapes FOUR characters, not five, because that is what the
// original did and this commit moves code rather than bettering it. Its output
// lands in element content only (a `<td>`, a `<pre>`, the mark), never inside
// an attribute — see `src/lib/htmlEscape.mjs` for the five-character escape
// and why the distinction is real.

export function escapeText(s) {
  return String(s == null ? "" : s).replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c],
  );
}

export function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString();
}

export function addrList(list) {
  if (!list?.length) return "—";
  return list
    .map((a) => a.address || a.name)
    .filter(Boolean)
    .join(", ");
}

// Phase 21.1 — neutralize inline cid: image sources in a rendered email body.
// Outlook embeds inline images with the `cid:` (Content-ID) scheme; in a
// browser iframe / html2pdf those resolve to nothing and trigger
// ERR_UNKNOWN_URL_SCHEME console noise + broken-image placeholders. We do NOT
// resolve them via Graph (out of scope) — we just strip the offending src so
// the browser never attempts the load. Covers `src` and `srcset` on any tag,
// both quote styles, case-insensitive. Leaves non-cid images untouched.
export function neutralizeCidImages(html) {
  if (!html || typeof html !== "string") return html;
  return html
    .replace(/\s(?:src|srcset)\s*=\s*(["'])\s*cid:[^"']*\1/gi, ' data-cid-blocked="1"')
    .replace(/\s(?:src|srcset)\s*=\s*cid:[^\s>]+/gi, ' data-cid-blocked="1"');
}

// The From / To / Cc / Date / Subject block both documents open with. `t` is
// `(key, fallback) => string`.
//
// THE KEYS ARE WRITTEN OUT IN FULL, and that is not verbosity. The first draft
// passed the caller a bare `"from"` and let it prepend `emailArchive.pdf.`;
// `scripts/lint/i18n-keys-complete.js` and `i18n-total.js` both resolve a key
// from the LITERAL at the call site, so seven keys became unresolvable the
// moment they moved into this file and both gates went red on strings that
// were seeded and correct. A key a gate cannot read is a key nobody is
// checking.
function headerTable(r, t) {
  const row = (label, value) =>
    value
      ? `<tr><td style="font-weight:700;padding:2px 10px 2px 0;white-space:nowrap;vertical-align:top">${escapeText(label)}</td><td>${escapeText(value)}</td></tr>`
      : "";
  return (
    `<table style="font-family:system-ui,Segoe UI,sans-serif;font-size:12px;border-collapse:collapse;margin:0 0 10px">` +
    row(t("emailArchive.pdf.from", "From"), r.from?.address || r.from?.name || "") +
    row(t("emailArchive.pdf.to", "To"), addrList(r.to) === "—" ? "" : addrList(r.to)) +
    (r.cc?.length ? row(t("emailArchive.pdf.cc", "Cc"), addrList(r.cc)) : "") +
    row(t("emailArchive.pdf.date", "Date"), fmtDate(r.date)) +
    row(t("emailArchive.pdf.subject", "Subject"), r.subject || "") +
    `</table>`
  );
}

const plainTextBlock = (r) =>
  `<pre style="white-space:pre-wrap;font-family:system-ui,Segoe UI,sans-serif;font-size:12px;margin:0">${escapeText(r.bodyText || r.preview || "")}</pre>`;

const page = (colors, inner) =>
  `<div style="padding:18px;font-family:system-ui,Segoe UI,sans-serif;font-size:13px;color:${colors.text};background:${colors.bg}">${inner}</div>`;

/**
 * The ordinary document: header, rule, then the email's own body.
 *
 * `body` is passed IN rather than read off `r`, because the colour strip that
 * makes it renderable belongs to the caller — `renderableColors.mjs` is a
 * separate concern from what a filed email looks like, and keeping them apart
 * is what lets the fixture drive the strip and the document independently.
 */
export function emailPdfDocument(r, body, { t, colors }) {
  const header = `${headerTable(r, t)}<hr style="border:none;border-top:1px solid ${colors.rule};margin:0 0 12px"/>`;
  return page(colors, `${header}${body}`);
}

/**
 * 170 §1a [RULED 2026-09-08, Laurent] — THE DEGRADED DOCUMENT.
 *
 * "An email whose HTML defeats the PDF converter is filed from its plain text
 *  instead of not filed at all. The degraded state is visible on the row and
 *  in the filed document."
 *
 * Same header, the same email, its plain text instead of its HTML — and A
 * MARK, because the whole reason to file a degraded document rather than none
 * is that somebody can later tell which archives are complete. The mark rides
 * IN THE DOCUMENT, not only in a toast that scrolls away.
 *
 * The reason is the converter's own message, escaped: it is the only thing
 * that says WHICH email defeated it and how, and a generic sentence in its
 * place would make every degraded archive look alike.
 */
export function emailTextPdfDocument(r, reason, { t, colors }) {
  const mark =
    `<div style="border:1px solid ${colors.rule};padding:8px 10px;margin:0 0 12px;font-size:11px;font-weight:700">` +
    `${escapeText(t("emailArchive.pdf.text_only", "Filed as text only — the original formatted message could not be converted to PDF."))}` +
    `<div style="font-weight:400;margin-top:3px">${escapeText(t("emailArchive.pdf.text_only_reason", "Reason"))}: ${escapeText(reason)}</div>` +
    `</div>`;
  const rule = `<hr style="border:none;border-top:1px solid ${colors.rule};margin:0 0 12px"/>`;
  return page(colors, `${headerTable(r, t)}${mark}${rule}${plainTextBlock(r)}`);
}

/** The marker the fixture and any later reader look for in a degraded file. */
export const TEXT_ONLY_MARK_KEY = "text_only";
